import { useLazyQuery } from '@apollo/client';
import { Order_By } from '@openx/types/gqlTypes.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAuth } from '../../../../../../../../../../components/AuthContext/context.ts';
import { GetDrawerSegmentsDocument } from '../../../../../../../../../../graphql/query/audiences/GetDrawerSegments.generated.tsx';
import { isAdmin } from '../../../../../../../../../../permissions/validators.ts';
import { exportType } from '../../../../../../../constants.ts';
import { AudienceFields, type AudienceForm, type FormSegment } from '../../../../../../../types.ts';
import { buildSegmentFilters } from '../../../../../hooks/utils/filterBuilder.ts';
import type { UseSegmentPagination } from '../../../hooks/types.ts';
import { dataSourceOptions } from '../../types.ts';
import type { SegmentFilters } from '../types.ts';
import { constructSegmentFilterParams } from '../utils/constructSegmentFilterParams.ts';
import { ActionType, createInitialPaginationState, paginationReducer } from './utils/paginationReducer.ts';

export const useSegmentPagination: UseSegmentPagination<FormSegment> = ({
  segments,
  accountId = '',
  linkedProviders,
  marketContext,
  searchPhrase,
  filters = {} as SegmentFilters,
  dataSourceFilters = dataSourceOptions.All,
}) => {
  const { control } = useFormContext<AudienceForm>();
  const { session } = useAuth();
  const observableExportType = useWatch({ name: AudienceFields.ExportType, control });

  const isOaMatchType = observableExportType === exportType.OA_MATCH;

  const [state, dispatch] = useReducer(
    paginationReducer<FormSegment>(),
    createInitialPaginationState<FormSegment>(segments || [], 0)
  );

  const [loadMoreSegments, { loading: loadingMore }] = useLazyQuery(GetDrawerSegmentsDocument);

  const prevFiltersRef = useRef(filters);
  const prevSearchPhraseRef = useRef(searchPhrase);
  const showMySegmentsOnly = dataSourceFilters === dataSourceOptions.MyData;
  const prevShowMySegmentsOnlyRef = useRef(dataSourceFilters === dataSourceOptions.MyData);
  const prevSegmentsRef = useRef<FormSegment[]>(segments || []);

  useEffect(() => {
    if (!isEqual(prevSegmentsRef.current, segments)) {
      dispatch({ type: ActionType.Reset, items: segments || [], totalCount: 0 });
      prevSegmentsRef.current = segments || [];
    }
  }, [segments]);

  useEffect(() => {
    const criteriaHasChanged =
      !isEqual(prevFiltersRef.current, filters) ||
      prevSearchPhraseRef.current !== searchPhrase ||
      prevShowMySegmentsOnlyRef.current !== showMySegmentsOnly;

    if (criteriaHasChanged) {
      dispatch({ type: ActionType.SetOffset, offset: 0 });

      prevFiltersRef.current = filters;
      prevSearchPhraseRef.current = searchPhrase;
      prevShowMySegmentsOnlyRef.current = showMySegmentsOnly;
    }
  }, [filters, searchPhrase, showMySegmentsOnly]);

  const handleLoadMore = useCallback(async () => {
    dispatch({ type: ActionType.StartLoading });

    const nextOffset = state.offset + 100;
    dispatch({ type: ActionType.SetOffset, offset: nextOffset });

    const segmentParams = constructSegmentFilterParams({
      filters,
      searchPhrase: searchPhrase || '',
    });
    const { where, whereAggregate } = buildSegmentFilters({
      filters: segmentParams,
      accountId,
      linkedProviders,
      marketContext,
      isOaMatchType,
      exportType: observableExportType ?? undefined,
      dataSourceFilters,
    });

    const accountIdVariables = isAdmin(session) ? { accountId } : {};

    try {
      const { data } = await loadMoreSegments({
        variables: {
          where,
          whereAggregate,
          limit: 100,
          offset: nextOffset,
          order_by: [{ category: Order_By.Desc }],
          ...accountIdVariables,
        },
      });
      dispatch({ type: ActionType.AppendItems, items: data?.segment || [] });
    } finally {
      dispatch({ type: ActionType.FinishLoading });
    }
  }, [
    loadMoreSegments,
    accountId,
    linkedProviders,
    state.offset,
    filters,
    searchPhrase,
    marketContext,
    isOaMatchType,
    observableExportType,
    dataSourceFilters,
    session,
  ]);

  return {
    loadingMore: state.isManualLoading || loadingMore,
    allSegments: state.items,
    totalCount: state.totalCount || 0,
    handleLoadMore,
  };
};
